/**
 * quoteStatus — shared predicates for a quote's lifecycle DIMENSIONS.
 *
 * Ruling of record (2026-07-30, restated in QuotesPage.js:28-34):
 *
 *   "Archived is a DIMENSION, not a status. A quote can be SENT and
 *    ARCHIVED simultaneously. The prior 'one primary chip per quote'
 *    ruling was unsatisfiable against real data."
 *
 * Same shape for converted: a quote can be Won (status) AND Converted
 * (dimension, carried on `convertedJobId`) simultaneously. Neither
 * archived nor converted is a value of `status`.
 *
 * N-14 writer discipline (2026-08-18, restated from 331_ §1):
 *
 *   archiveQuote (cmd/quotes/main.go:882) sets `archivedAt` and NEVER
 *   touches `status`. A UI-archived quote keeps `status: 'active'`
 *   forever. An exclusion written against `q.status` alone would
 *   satisfy the letter of the ruling and still count every archived
 *   quote as "open." That is the whole reason this module exists.
 *
 * Consumers of this module:
 *   - QuotesPage (main listing filter — was carrying local copies)
 *   - QuotesPage.DEF-037.test.js (was carrying its own local copies)
 *   - dashboardData.js computeKPIs (openQuotes tile — PR-41)
 *
 * Do not add fallback readers here. Each predicate names its canonical
 * key AND its snake_case alias — that pair is the whole vocabulary.
 * Adding a third alias is the shape of drift that put a wrong dollar
 * figure on the dashboard in the first place.
 */

import { isConverted } from './isConverted';

// Re-export so callers have a single import surface for quote-lifecycle
// predicates. Do not re-implement.
export { isConverted };

/**
 * isArchived — true when the quote's `archivedAt` timestamp is set.
 *
 * Writer: backend-go/cmd/quotes/main.go:882 (archiveQuote):
 *   existing["archivedAt"] = now
 *   existing["archivedBy"] = tc.UserID
 *   // status is NEVER touched
 *
 * The snake_case alias is preserved for records that may have arrived
 * through an older projection.
 */
export function isArchived(q) {
  if (!q) return false;
  return !!(q.archivedAt || q.archived_at);
}

/**
 * isSent — true when the quote has been sent for signature at any point.
 *
 * Two positive-cases:
 *   - `signatureSentAt` / `signature_sent_at` is set (the modern path)
 *   - `status === 'sent'` (legacy records where signatureSentAt was not
 *     yet a field)
 */
export function isSent(q) {
  if (!q) return false;
  return !!(
    q.signatureSentAt ||
    q.signature_sent_at ||
    (q.status || '').toLowerCase() === 'sent'
  );
}

// ─── Membership sets — 00_OPERATOR_RULINGS.md §3t-ii, A1 ───────────────────
//
// Named exports so a future ruling change is a one-line edit here, not a
// re-spec across every consumer.

/**
 * QUOTE_OPEN_STATUSES — the STATUSES a quote may have and still count as open.
 *
 * Derived from operator ruling A1 (2026-08-18). The operator's own words,
 * verbatim: "converted, expired, lost, archived should not show as an open
 * quote." (00_OPERATOR_RULINGS.md §A1.)
 *
 * Derivation:
 *   - `converted` and `archived` are DIMENSIONS carried on their own fields
 *     (convertedJobId, archivedAt), not values of status. Enforced separately
 *     via isConverted / isArchived below.
 *   - `expired` and `lost` are status values — just not in this allow-list.
 *   - The remaining statuses (`active`, `draft`, `sent`, `viewed`, `won`) are
 *     what "open" resolves to.
 *   - `won` staying in the open set is deliberate: a won-but-not-yet-converted
 *     quote is accepted work that hasn't become a job. isConverted drops it
 *     the moment it does. (This last clause is my derivation, not the
 *     operator's words. Ruling was on the four-item exclusion; the
 *     open-list membership follows from that plus the dimensions.)
 */
// R-03 (2026-08-18, ruling §3ag): open = active + sent. Nothing else.
// Previously included draft/viewed/won which put ~1,167 won quotes (~49% of
// tenant 270's book) in the tile, producing the $27,738,108.00 headline.
//
// Ordering hazard called out in §1: this is a TILE predicate change only.
// IDI-69 (Tyler) migrates draft → active on live records; until that lands,
// draft is still a value on live rows. Removing it here is safe (the tile
// simply omits those rows from the open bucket) but do NOT touch
// STATUS_PREDICATES.active in QuotesPage.js until IDI-69 confirms done.
export const QUOTE_OPEN_STATUSES = new Set(['active', 'sent']);

/**
 * isQuoteOpen — the tile predicate.
 *
 * A quote is "open" when its status is in QUOTE_OPEN_STATUSES AND it is
 * neither archived nor converted (the two dimensions). Any other value of
 * status (expired, lost, or something we don't recognise) is not open.
 *
 * IDI-146 (2026-09-08): isQuoteOpen is DISTINCT from isCanonicalActive /
 * isCanonicalSent below. They answer different questions:
 *
 *   · isQuoteOpen — "is this quote in the OPEN BOOK" (spans Active AND
 *     Sent as one class). Powers the "Open Quotes" DASHBOARD TILE.
 *   · isCanonicalActive / isCanonicalSent — "which BUCKET does this quote
 *     belong to." Powers the QUOTES PAGE CHIP counts and the DASHBOARD
 *     QUOTES PIPELINE segments. Mutually exclusive on the non-archived,
 *     non-converted population.
 *
 * Do NOT fold isQuoteOpen into the bucket predicates. They agree today
 * (open == active-or-sent) but they are semantically different reads;
 * merging them would collapse a distinction the tile depends on.
 */
export function isQuoteOpen(q) {
  if (!q) return false;
  if (isConverted(q)) return false;
  if (isArchived(q)) return false;
  const status = (q.status || '').toLowerCase();
  return QUOTE_OPEN_STATUSES.has(status);
}

// ─── IDI-146 (2026-09-08) · canonical bucket predicates ─────────────────
//
// Extracted from QuotesPage.STATUS_PREDICATES.active/sent (:56-64) and
// dashboardData.computeQuotesPipeline (:226-227). Pre-extraction:
//
//   QuotesPage chip:      !isSent(q) && (q.status === 'active' || q.status === 'draft')
//   dashboardData active: statusLower(q) === 'active'
//   [and the parallel `sent` variants]
//
// The chip's semantics were correct on the isSent axis (a sent quote
// belongs in the Sent bucket, not Active) and on the draft axis (a draft
// quote is real work in the book, counts as Active). The pipeline's
// case-handling was correct (`Active` should not silently drop out of
// the list; ruling: uppercase status is a data condition, not a bug in
// the row). Both current implementations were wrong on different axes;
// the canonical form below combines the correct half of each.
//
// Operator ruling (2026-09-08):
//   canonical active(q) = !isSent(q) && ['active','draft'].includes(statusLower(q))
//
// Neither symptom reproduces on tenants 270 or Sample Dealer today (both
// carry zero drafts, zero isSent quotes, zero uppercase statuses). The
// isSent axis in particular is UNTESTABLE on every environment we have
// because the send flow hasn't been exercised yet — soft launch is when
// dealers start sending quotes. The divergence is queued behind a feature
// about to go live; this extraction closes the class before then.
//
// The conformance tests in quoteStatus.idi146.test.js include DELIBERATE
// fixtures with a draft quote, an isSent-with-status-active quote, and
// an uppercase-status quote. Each must go RED against the pre-extraction
// predicates (a conformance suite green against the pre-fix code proves
// nothing — the #370 trap).
//
// Failure modes attached:
//   · Folding isQuoteOpen into isCanonicalActive would collapse the
//     bucket distinction into an open-book class. Do not (see docblock
//     on isQuoteOpen above).
//   · Case-handling: statusLower matters. A future writer that starts
//     emitting 'Active' or 'DRAFT' MUST route through this predicate —
//     inlining `q.status === 'active'` at a caller would drop those
//     rows silently.
//   · Ordering hazard: this canonical set includes 'draft'. IDI-69
//     (Tyler) migrates draft → active on live records; when that lands,
//     the draft term becomes dead code here, harmless. Do NOT drop it
//     preemptively — until the migration confirms zero draft rows on
//     the wire, the predicate must accept both.
//
// Consumers (docblock-conformance-test enforced):
//   · QuotesPage.js STATUS_PREDICATES.active + .sent
//   · dashboardData.computeQuotesPipeline active + sent
//
// If a third consumer appears (list surface, filter chip, tile), it
// imports from THIS file. Adding it here without a caller MUST NOT be
// silent — see the docblock-consumer conformance test.

const QUOTE_ACTIVE_STATUSES = new Set(['active', 'draft']);

// IDI-392: statuses that mean a quote's lifecycle has concluded — once here,
// a quote is no longer canonically "Sent" even if it still carries a
// historical signatureSentAt from before it won/lost/expired.
const QUOTE_TERMINAL_STATUSES = new Set(['won', 'lost', 'expired']);

function parseTs(v) {
  if (!v) return NaN;
  const t = Date.parse(v);
  return t;
}

/**
 * hasCurrentSend — IDI-568 (D1): true when the quote's latest send still
 * stands. status 'sent' always counts. Otherwise the latest send
 * (resentAt, else signatureSentAt) must exist and must not predate
 * reopenedAt. A reopened, unsent quote is therefore not sent. isSent()
 * keeps its historical meaning (sent at any point).
 * Mirrored exactly by backend-go/cmd/quotes hasCurrentSend.
 */
export function hasCurrentSend(q) {
  if (!q) return false;
  if ((q.status || '').toLowerCase() === 'sent') return true;
  const latest = q.resentAt || q.signatureSentAt || q.signature_sent_at;
  if (!latest) return false;
  if (!q.reopenedAt) return true;
  const sentT = parseTs(latest);
  const reopenT = parseTs(q.reopenedAt);
  if (Number.isNaN(sentT) || Number.isNaN(reopenT)) return true;
  return sentT >= reopenT;
}

export function isCanonicalActive(q) {
  if (!q) return false;
  if (hasCurrentSend(q)) return false;
  const status = (q.status || '').toLowerCase();
  return QUOTE_ACTIVE_STATUSES.has(status);
}

export function isCanonicalSent(q) {
  if (!q) return false;
  // IDI-392: isSent() is a HISTORICAL/audit signal — signatureSentAt is set
  // the moment a signature request goes out and NEVER cleared, even after the
  // quote is later won, lost, or converted to a job. Before this guard, a
  // won-and-signed quote (status: 'won', signatureSentAt set, signatureStatus:
  // 'acknowledged', convertedJobId set) satisfied isSent() and was counted as
  // "Sent" on BOTH the Dashboard pipeline AND the Quotes page chip, on top of
  // its own 'won' bucket — the exact double-count that made the pipeline's
  // segments sum to more than its own total (116 vs 115, measured live on
  // tenant 1: quote bf345671...). A quote that has moved to a TERMINAL status
  // other than 'sent' is no longer canonically "Sent", regardless of its
  // signature-sent history — mirrors isCanonicalActive's own isSent() guard,
  // just from the other direction.
  const status = (q.status || '').toLowerCase();
  if (status && QUOTE_TERMINAL_STATUSES.has(status)) return false;
  // IDI-568: a send that predates a Reopen no longer counts.
  return hasCurrentSend(q);
}
